import { useEffect } from "react"
import { useLocation } from "wouter"
import { useTranslation } from "react-i18next"
import { useAdminAuth } from "@/hooks/useAdminAuth"
import { ReconsentModal } from "@/components/admin/ReconsentModal"

export const AdminGuard = ({ children }: { children: React.ReactNode }) => {
    const { user, isLoading } = useAdminAuth()
    const [, navigate] = useLocation()
    const { t } = useTranslation()

    useEffect(() => {
        if (!isLoading && !user) {
            // ~ prefix = absolute path in wouter nested route context
            navigate("~/admin/login", { replace: true })
        }
    }, [isLoading, user, navigate])

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-slate-400 font-bold">{t("common.loading")}</div>
            </div>
        )
    }

    if (!user) return null

    // Blocking re-consent modal: rendered over the regular admin UI when
    // the user's stored consent version doesn't match the current
    // document version. Backfilled "pre-launch" rows hit this path first,
    // and any future revision of VOP/Privacy will trigger it again.
    const reconsent = user.needsReconsent
    const showReconsent = !!reconsent && (reconsent.terms || reconsent.privacy)

    return (
        <>
            {children}
            {showReconsent && reconsent && (
                <ReconsentModal needsTerms={reconsent.terms} needsPrivacy={reconsent.privacy} />
            )}
        </>
    )
}
